import classNames from 'classnames'
import { useField } from 'remix-validated-form'
import FormToggle from 'app/components/form/FormToggle'
import LocationSelector from 'app/components/LocationSelector'
import { legacyRoleRank } from 'app/types/roles'
import type { LocationJson, LocationType } from 'app/types/entities'
import type { UserJson } from 'app/types/entities'

type Props = {
  user: UserJson
  role: number
  allLocations: boolean
  allowedLocations: LocationType[]
  setAllLocations: (value: boolean) => void
  setAllowedLocations: (locations: LocationType[]) => void
  onAllowedLocationChange: (location: LocationJson, index?: number) => void
  removeAllowedLocation: (location: LocationJson) => void
  handleAllLocationsChange: (isOn: boolean) => void
  isNewWorkspaceUser: boolean
  isOrgLevel: boolean
  getFilterLocations: { [key: string]: string } | undefined
}

export default function RequiredLocationSelector({
  user,
  role,
  allLocations,
  allowedLocations,
  setAllLocations,
  setAllowedLocations,
  onAllowedLocationChange,
  removeAllowedLocation,
  handleAllLocationsChange,
  isNewWorkspaceUser,
  isOrgLevel,
  getFilterLocations,
}: Props) {
  const { error } = useField(`allowedLocationIds`)

  // Original V1 default value logic
  let getDefaultValue = () => {
    if (isOrgLevel) return !user.assignedLocationIds?.length
    return (
      role === legacyRoleRank.workspaceOwner ||
      (!isNewWorkspaceUser && role !== legacyRoleRank.workspaceOwner && !user?.allowedLocationIds?.length)
    )
  }

  return (
    <div className="col-span-6">
      <label
        htmlFor="allowedLocationIds"
        className={classNames(`text-ui-sm text-strong`, `required`, error && `text-red-200`)}
      >
        {`${isOrgLevel ? `Assigned ` : `Allowed`} Locations`}
      </label>
      <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
        <div className="flex w-full items-start gap-2">
          <label
            className={classNames(
              `flex items-center justify-center gap-3 rounded-lg border border-default bg-surface-subtle p-[0.55rem] text-ui-sm text-strong`,
            )}
          >
            All Locations{` `}
            <FormToggle
              name="allLocations"
              label="All"
              defaultValue={getDefaultValue()}
              readOnly={role === legacyRoleRank.workspaceOwner}
              onToggle={(isOn) => {
                setAllLocations(isOn)
                if (isOn) {
                  setAllLocations(true)
                  setAllowedLocations([])
                } else {
                  setAllLocations(false)
                }
                handleAllLocationsChange(isOn)
              }}
            />
          </label>
          <div className="h-full grow py-0">
            <LocationSelector
              withBlankOption={false}
              filterLocations={getFilterLocations}
              disabled={allLocations}
              selectedLocations={allowedLocations as LocationJson[]}
              onChange={(location) => onAllowedLocationChange(location)}
              onRemoveSelectedOption={(location) => {
                removeAllowedLocation(location)
              }}
            />
            {allLocations && (
              <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
                <div className="inline-flex gap-2">
                  <span className="flex items-baseline justify-center gap-3 rounded border border-default bg-surface-subtle p-2 text-body-sm text-strong">
                    All
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {error && <div className={classNames(`mt-[9px] text-body-sm text-red-200`)}>{error}</div>}
    </div>
  )
}
